import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Vendor intelligence (T-0267; E14-design §3). Routes in
 * `apps/api/src/routes/vendor-intelligence.ts`.
 *
 *   - The evaluation (`GET`) carries everything the tab shows: the products
 *     in scope, the evaluated modules with their weights, the live ratings
 *     with their citations, the judge's last run per cell, and the fit with
 *     its sensitivity cases. Every figure is the server's
 *     (@caliber-2/vendor-fit); the web computes none.
 *   - Propose answers with the time it asked (the database clock) and the
 *     items sent to the judge; the tab polls the evaluation until each of
 *     them has a newer rating or a finished run.
 */

export const RATINGS = ['strong', 'partial', 'gap', 'unknown'] as const;
export type Rating = (typeof RATINGS)[number];

/**
 * What a product becomes in the report (T-0273; ADR-026 decision 3): code
 * proposes the top three as shortlisted, the other ranked products as
 * alternates and the unrated ones as not assessed; a consultant confirms
 * the proposal or changes a row with a reason.
 */
export const DISPOSITIONS = ['shortlisted', 'alternate', 'set_aside', 'not_assessed'] as const;
export type Disposition = (typeof DISPOSITIONS)[number];
export type DispositionStatus = 'proposed' | 'confirmed';

const NamedSchema = z.object({ id: z.string(), name: z.string() });

const CountsSchema = z.object({ confirmed_items: z.number().int(), answers: z.number().int() });

export const ProductInScopeSchema = CountsSchema.extend({
  product_id: z.string(),
  name: z.string(),
  platform_type: z.string(),
  status: z.string(),
  vendor: NamedSchema,
  from_list: NamedSchema.nullable(),
  added_at: z.string(),
  /** Null until the fit first proposes one (a row from before the dispositions deploy). */
  disposition: z.enum(DISPOSITIONS).nullable(),
  disposition_status: z.enum(['proposed', 'confirmed']).nullable(),
  /** Why a consultant set it apart from the proposal; confirmed rows only. */
  disposition_reason: z.string().nullable(),
  disposition_by: NamedSchema.nullable(),
  disposition_at: z.string().nullable(),
});
export type ProductInScope = z.infer<typeof ProductInScopeSchema>;

export const EvaluatedModuleSchema = z.object({
  module_id: z.string(),
  name: z.string(),
  weight: z.number(),
  counts: z.object({ critical: z.number(), desired: z.number(), not_set: z.number() }),
  requirements: z.number(),
});
export type EvaluatedModule = z.infer<typeof EvaluatedModuleSchema>;

/** A knowledge item a rating rests on, rendered by the server for the firm's own audience. */
export const RatingCitationSchema = z.object({
  kind: z.literal('vendor_knowledge_item'),
  item_id: z.string(),
  number: z.string().nullable(),
  item_kind: z.string(),
  statement: z.string().nullable(),
  answer_code: z.string().nullable(),
  answer_label: z.string().nullable(),
  excerpt: z.string().nullable(),
  label: z.string(),
  source_label: z.string(),
  date: z.string(),
  url: z.string().nullable(),
  locator_label: z.string().nullable(),
  confidential: z.boolean(),
});
export type RatingCitation = z.infer<typeof RatingCitationSchema>;

export const AnswersSummarySchema = z.object({
  counts: z.object({ S: z.number(), C: z.number(), T: z.number(), F: z.number(), N: z.number() }),
  requirements: z.number(),
  answered: z.number(),
  module_weight: z.number(),
  answered_weight: z.number(),
  score: z.number().nullable(),
});
export type AnswersSummary = z.infer<typeof AnswersSummarySchema>;

export const RatingSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  module_id: z.string(),
  rating: z.enum(RATINGS),
  source: z.enum(['vendor_answers', 'knowledge_items', 'consultant']),
  status: z.enum(['proposed', 'confirmed', 'superseded']),
  citations: z.array(RatingCitationSchema),
  answers_summary: AnswersSummarySchema.nullable(),
  rationale: z.string().nullable(),
  change_reason: z.string().nullable(),
  confirmed_by: NamedSchema.nullable(),
  confirmed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type VendorRating = z.infer<typeof RatingSchema>;

export const JudgeRunSchema = z.object({
  product_id: z.string(),
  module_id: z.string(),
  status: z.enum(['running', 'succeeded', 'skipped', 'failed']),
  reason: z.string().nullable(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
});
export type JudgeRun = z.infer<typeof JudgeRunSchema>;

export const ProductFitSchema = z.object({
  product_id: z.string(),
  name: z.string(),
  points: z.number(),
  index: z.number(),
  coverage: z.number(),
  confirmed_modules: z.number(),
  modules: z.number(),
  rank: z.number().nullable(),
  /** The other ranked products with the same index: a tie, never broken. */
  tied_with: z.array(z.string()),
  top: z.boolean(),
});
export type ProductFit = z.infer<typeof ProductFitSchema>;

export const SensitivityChangeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('passes'), above: z.string(), below: z.string() }),
  z.object({ kind: z.literal('enters'), product_id: z.string() }),
  z.object({ kind: z.literal('leaves'), product_id: z.string() }),
]);
export type SensitivityChange = z.infer<typeof SensitivityChangeSchema>;

export const SensitivityCaseSchema = z.object({
  key: z.string(),
  label: z.string(),
  applicable: z.boolean(),
  products: z.array(
    z.object({ product_id: z.string(), index: z.number(), rank: z.number().nullable() }),
  ),
  top: z.array(z.string()),
  holds: z.boolean(),
  /** holds; tied (only ties, nothing strictly changed); changes; not_applicable. */
  outcome: z.enum(['holds', 'tied', 'changes', 'not_applicable']),
  tied: z.array(z.tuple([z.string(), z.string()])),
  changes: z.array(SensitivityChangeSchema),
});
export type SensitivityCase = z.infer<typeof SensitivityCaseSchema>;

export const FitSchema = z.object({
  total_weight: z.number(),
  products: z.array(ProductFitSchema),
  top: z.array(z.string()),
  /** Each group of ranked products sharing an index. */
  ties: z.array(z.array(z.string())),
  sensitivity: z.object({
    base_top: z.array(z.string()),
    cases: z.array(SensitivityCaseSchema),
    holds: z.number(),
    tied: z.number(),
    changed: z.number(),
    checks: z.number(),
  }),
  /** Every product in scope has a confirmed disposition (false with no product). */
  dispositions_confirmed: z.boolean(),
});
export type VendorFit = z.infer<typeof FitSchema>;

/**
 * The confirmed module ratings rolled up by client area (T-0275;
 * E24-design §4; ADR-026 decision 4). Computed on read by the server's
 * deriver, like the fit; the web shows it and computes nothing.
 */
export const AreaModuleRatingSchema = z.object({
  module_id: z.string(),
  /** The weight of the area's requirements mapped to this module. */
  weight_in_area: z.number(),
  /** The product's confirmed rating in the module; null when it has none. */
  rating: z.enum(RATINGS).nullable(),
});
export type AreaModuleRating = z.infer<typeof AreaModuleRatingSchema>;

export const AreaProductRatingSchema = z.object({
  product_id: z.string(),
  points: z.number(),
  /** points / weight, 0 to 1. */
  value: z.number(),
  rating: z.enum(RATINGS),
  /** The share of the area's weight whose module rating is Unknown or missing, 0 to 1. */
  unknown_share: z.number(),
  /** The module ratings behind the cell. */
  modules: z.array(AreaModuleRatingSchema),
});
export type AreaProductRating = z.infer<typeof AreaProductRatingSchema>;

export const ClientAreaSchema = z.object({
  key: z.string(),
  name: z.string(),
  weight: z.number(),
  counts: z.object({
    critical: z.number(),
    desired: z.number(),
    not_set: z.number(),
    not_required: z.number(),
    not_applicable: z.number(),
  }),
  /** Weighted requirements in the area with no module: out of the weight. */
  unmapped: z.number(),
  /** One per product in scope, in the fit's order. */
  products: z.array(AreaProductRatingSchema),
});
export type ClientArea = z.infer<typeof ClientAreaSchema>;

export const NotAssessedAreaSchema = z.object({
  key: z.string(),
  name: z.string(),
  requirements: z.number(),
  critical: z.number(),
});
export type NotAssessedArea = z.infer<typeof NotAssessedAreaSchema>;

/**
 * Requirements with no library module, by client area: not assessed until
 * mapped (owner decision, 24 October 2026). Absent from an older server.
 */
export const NotAssessedSchema = z.object({
  areas: z.array(
    z.object({ area: z.string().nullable(), requirements: z.number(), critical: z.number() }),
  ),
  requirements: z.number(),
  critical: z.number(),
});
export type NotAssessed = z.infer<typeof NotAssessedSchema>;

export const VendorIntelligenceSchema = z.object({
  engagement_id: z.string(),
  products: z.array(ProductInScopeSchema),
  modules: z.array(EvaluatedModuleSchema),
  /** The confirmed ratings by client area, largest weight first, "Other" last. */
  areas: z.array(ClientAreaSchema),
  /** Areas with weighted requirements that have no module: not assessed by area. */
  not_assessed_by_area: z.array(NotAssessedAreaSchema),
  ratings: z.array(RatingSchema),
  judge_runs: z.array(JudgeRunSchema),
  fit: FitSchema,
  not_assessed: NotAssessedSchema.optional(),
  counts: z.object({
    cells: z.number(),
    proposed: z.number(),
    confirmed: z.number(),
    unrated: z.number(),
  }),
  can_edit: z.boolean(),
});
export type VendorIntelligence = z.infer<typeof VendorIntelligenceSchema>;

export const AvailableProductSchema = CountsSchema.extend({
  product_id: z.string(),
  name: z.string(),
  platform_type: z.string(),
  status: z.string(),
  vendor: NamedSchema,
  addable: z.boolean(),
  in_scope: z.boolean(),
  position: z.number().nullable(),
});
export type AvailableProduct = z.infer<typeof AvailableProductSchema>;

export const AvailableProductsSchema = z.object({
  list: z
    .object({
      id: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      archived: z.boolean(),
    })
    .nullable(),
  products: z.array(AvailableProductSchema),
});
export type AvailableProducts = z.infer<typeof AvailableProductsSchema>;

const CellSchema = z.object({ product_id: z.string(), module_id: z.string() });
export type RatingCell = z.infer<typeof CellSchema>;

export const ProposeResultSchema = z.object({
  requested_at: z.string(),
  derived: z.array(CellSchema.extend({ rating: z.enum(RATINGS), rating_id: z.string() })),
  requested: z.array(CellSchema),
  skipped_confirmed: z.number(),
});
export type ProposeResult = z.infer<typeof ProposeResultSchema>;

const ConfirmManySchema = z.object({
  confirmed: z.array(RatingSchema),
  skipped: z.array(z.string()),
});

export interface ProductChoice {
  product_id: string;
  from_list_id?: string | null;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function base(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/vendor-intelligence`;
}

export function getVendorIntelligence(engagementId: string): Promise<VendorIntelligence> {
  return apiClient(base(engagementId), VendorIntelligenceSchema);
}

export function setVendorProducts(
  engagementId: string,
  products: readonly ProductChoice[],
): Promise<VendorIntelligence> {
  return apiClient(`${base(engagementId)}/products`, VendorIntelligenceSchema, {
    method: 'PUT',
    body: { products },
  });
}

export function getAvailableProducts(
  engagementId: string,
  listId: string | null,
): Promise<AvailableProducts> {
  return apiClient(`${base(engagementId)}/products:available`, AvailableProductsSchema, {
    query: listId ? { list_id: listId } : undefined,
  });
}

export function proposeRatings(
  engagementId: string,
  input: { product_ids?: string[]; module_ids?: string[]; repropose?: boolean } = {},
): Promise<ProposeResult> {
  return apiClient(`${base(engagementId)}:propose`, ProposeResultSchema, {
    method: 'POST',
    body: input,
  });
}

export function confirmRating(engagementId: string, ratingId: string): Promise<VendorRating> {
  return apiClient(
    `${base(engagementId)}/ratings/${encodeURIComponent(ratingId)}:confirm`,
    RatingSchema,
    { method: 'POST' },
  );
}

export function confirmRatings(engagementId: string, ratingIds: readonly string[]) {
  return apiClient(`${base(engagementId)}/ratings:confirm-many`, ConfirmManySchema, {
    method: 'POST',
    body: { rating_ids: ratingIds },
  });
}

export function changeRating(
  engagementId: string,
  ratingId: string,
  input: { rating: Rating; reason: string },
): Promise<VendorRating> {
  return apiClient(`${base(engagementId)}/ratings/${encodeURIComponent(ratingId)}`, RatingSchema, {
    method: 'PATCH',
    body: input,
  });
}

/** One row of `PUT /vendor-intelligence/dispositions`: a reason only when it differs from the proposal. */
export interface DispositionRow {
  product_id: string;
  disposition: Disposition;
  reason?: string;
}

/**
 * Decides the rows sent (the others stay as they are); each becomes
 * confirmed with who and when. The server needs a reason of at least ten
 * characters on a row that differs from its proposal. Returns the whole
 * evaluation, like a products write.
 */
export function setDispositions(
  engagementId: string,
  rows: readonly DispositionRow[],
): Promise<VendorIntelligence> {
  return apiClient(`${base(engagementId)}/dispositions`, VendorIntelligenceSchema, {
    method: 'PUT',
    body: rows,
  });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const vendorIntelligenceKeys = {
  all: (engagementId: string) => ['vendor-intelligence', engagementId] as const,
  evaluation: (engagementId: string) =>
    [...vendorIntelligenceKeys.all(engagementId), 'evaluation'] as const,
  available: (engagementId: string, listId: string | null) =>
    [...vendorIntelligenceKeys.all(engagementId), 'available', listId ?? 'all'] as const,
};

/** How often the evaluation is read again while the judge proposes ratings. */
export const PROPOSE_POLL_MS = 2000;

export function useVendorIntelligenceQuery(engagementId: string, opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: vendorIntelligenceKeys.evaluation(engagementId),
    queryFn: () => getVendorIntelligence(engagementId),
    refetchInterval: opts.poll ? PROPOSE_POLL_MS : false,
  });
}

export function useAvailableProducts(
  engagementId: string,
  listId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: vendorIntelligenceKeys.available(engagementId, listId),
    queryFn: () => getAvailableProducts(engagementId, listId),
    enabled,
  });
}

function useEvaluationWrite<TArgs, TResult>(
  engagementId: string,
  fn: (args: TArgs) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (result) => {
      if (result && typeof result === 'object' && 'fit' in result) {
        qc.setQueryData(vendorIntelligenceKeys.evaluation(engagementId), result);
      }
      return qc.invalidateQueries({ queryKey: vendorIntelligenceKeys.all(engagementId) });
    },
  });
}

export function useSetVendorProducts(engagementId: string) {
  return useEvaluationWrite(engagementId, (products: readonly ProductChoice[]) =>
    setVendorProducts(engagementId, products),
  );
}

export function useProposeRatings(engagementId: string) {
  return useEvaluationWrite(
    engagementId,
    (input: { product_ids?: string[]; module_ids?: string[]; repropose?: boolean }) =>
      proposeRatings(engagementId, input),
  );
}

export function useConfirmRating(engagementId: string) {
  return useEvaluationWrite(engagementId, (ratingId: string) =>
    confirmRating(engagementId, ratingId),
  );
}

export function useConfirmRatings(engagementId: string) {
  return useEvaluationWrite(engagementId, (ratingIds: readonly string[]) =>
    confirmRatings(engagementId, ratingIds),
  );
}

export function useChangeRating(engagementId: string) {
  return useEvaluationWrite(
    engagementId,
    (args: { ratingId: string; rating: Rating; reason: string }) =>
      changeRating(engagementId, args.ratingId, { rating: args.rating, reason: args.reason }),
  );
}

export function useSetDispositions(engagementId: string) {
  return useEvaluationWrite(engagementId, (rows: readonly DispositionRow[]) =>
    setDispositions(engagementId, rows),
  );
}
